import { ExternalLink } from 'lucide-react';
import { memo, type ReactElement, type ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrev,
  InlineCitationSource,
  InlineCitationText,
} from '../../ai-elements';
import { createComponents } from '../../ui/markdown-components';
import { getSourceMetadata, type SourceUrlPart, type SourcesMap } from '../lib/sources';

interface MarkdownWithCitationsProps {
  children: string;
  sourcesMap: SourcesMap;
  messageUuid?: string;
  className?: string;
}

// Pattern to match [SOURCE-<id>] where <id> is any non-bracket characters
const CITATION_PATTERN = /\[SOURCE-([^\]]+)\]/g;

/**
 * Renders a single inline citation with hover card
 */
interface CitationProps {
  source: SourceUrlPart;
  index: number;
  citationKey: string;
}

function Citation({ source, index, citationKey }: CitationProps) {
  const metadata = getSourceMetadata(source);

  return (
    <InlineCitation key={citationKey}>
      <InlineCitationText />
      <InlineCitationCard>
        <InlineCitationCardTrigger sources={[source.url]}>{index}</InlineCitationCardTrigger>
        <InlineCitationCardBody>
          <InlineCitationCarousel>
            <InlineCitationCarouselHeader>
              <InlineCitationCarouselPrev />
              <InlineCitationCarouselIndex />
              <InlineCitationCarouselNext />
            </InlineCitationCarouselHeader>
            <InlineCitationCarouselContent>
              <InlineCitationCarouselItem>
                <InlineCitationSource title={source.title} description={metadata?.textSnippet}>
                  {/* Clickable external link icon next to filename */}
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary absolute top-0 right-0 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>

                  {/* Show file date if available */}
                  {metadata?.fileDate && (
                    <div className="text-muted-foreground text-xs">
                      Date: {new Date(metadata.fileDate).toLocaleDateString()}
                    </div>
                  )}
                  {/* Show email sender if available */}
                  {metadata?.emailFrom && (
                    <div className="text-muted-foreground text-xs">From: {metadata.emailFrom}</div>
                  )}
                </InlineCitationSource>
              </InlineCitationCarouselItem>
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
}

/**
 * Parse text and replace [SOURCE-<id>] markers with citation components.
 * Citation numbers are determined by the source's position in the sourceIndexMap (1-based).
 */
function parseTextWithCitations(
  text: string,
  sourcesMap: SourcesMap,
  sourceIndexMap: Map<string, number>,
): (string | ReactElement)[] {
  const parts: (string | ReactElement)[] = [];
  let lastIndex = 0;
  let citationIndex = 0;

  // Reset lastIndex to ensure regex starts from beginning
  CITATION_PATTERN.lastIndex = 0;

  let match = CITATION_PATTERN.exec(text);
  while (match !== null) {
    const matchIndex = match.index;
    const sourceId = match[1];

    // Add text before the citation
    if (matchIndex > lastIndex) {
      parts.push(text.substring(lastIndex, matchIndex));
    }

    // Get the source for this citation
    const source = sourcesMap.get(sourceId);

    if (source) {
      // Get the 1-based index for this source
      const sourceIndex = sourceIndexMap.get(sourceId) ?? 0;
      const currentCitationIndex = citationIndex;
      citationIndex += 1;
      parts.push(
        <Citation
          key={`citation-${sourceId}-${currentCitationIndex}`}
          source={source}
          index={sourceIndex}
          citationKey={`citation-${sourceId}-${currentCitationIndex}`}
        />,
      );
    } else {
      // If source not found, just keep the marker
      parts.push(match[0]);
    }

    lastIndex = CITATION_PATTERN.lastIndex;
    match = CITATION_PATTERN.exec(text);
  }

  // Add remaining text after the last citation
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts;
}

/**
 * Recursively process children to find and replace text nodes containing citations
 */
function processChildren(children: ReactNode, sourcesMap: SourcesMap, sourceIndexMap: Map<string, number>): ReactNode {
  if (typeof children === 'string') {
    return parseTextWithCitations(children, sourcesMap, sourceIndexMap);
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        const parsed = parseTextWithCitations(child, sourcesMap, sourceIndexMap);
        // eslint-disable-next-line react/no-array-index-key
        return parsed.length === 1 ? parsed[0] : <span key={index}>{parsed}</span>;
      }
      return child;
    });
  }

  return children;
}

/**
 * Create markdown components with citation support
 */
function createCitationComponents(
  sourcesMap: SourcesMap,
  sourceIndexMap: Map<string, number>,
  messageUuid?: string,
): Partial<Components> {
  const baseComponents = createComponents(messageUuid);

  return {
    ...baseComponents,
    // Override the paragraph component to handle citations in text
    p: ({ children: pChildren, ...props }) => {
      const processedChildren = processChildren(pChildren, sourcesMap, sourceIndexMap);
      return (
        <p className="my-4" {...props}>
          {processedChildren}
        </p>
      );
    },
    // Also handle text in other common elements
    li: ({ children: liChildren, ...props }) => {
      const processedChildren = processChildren(liChildren, sourcesMap, sourceIndexMap);
      return (
        <li className="py-1" {...props}>
          {processedChildren}
        </li>
      );
    },
    strong: ({ children: strongChildren, ...props }) => {
      const processedChildren = processChildren(strongChildren, sourcesMap, sourceIndexMap);
      return (
        <span className="font-semibold" {...props}>
          {processedChildren}
        </span>
      );
    },
    em: ({ children: emChildren, ...props }) => {
      const processedChildren = processChildren(emChildren, sourcesMap, sourceIndexMap);
      return <em {...props}>{processedChildren}</em>;
    },
  };
}

const remarkPlugins = [remarkGfm];

function NonMemoizedMarkdownWithCitations({
  children,
  sourcesMap,
  messageUuid,
  className,
}: MarkdownWithCitationsProps) {
  // Create sources array (preserves insertion order)
  const sourcesArray = useMemo(() => Array.from(sourcesMap.values()), [sourcesMap]);

  // Create sourceId -> 1-based index map for O(1) lookup during citation parsing
  const sourceIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sourcesArray.forEach((source, index) => {
      map.set(source.sourceId, index + 1);
    });
    return map;
  }, [sourcesArray]);

  // Memoize components object to avoid recreation on every render
  const components = useMemo<Partial<Components>>(
    () => createCitationComponents(sourcesMap, sourceIndexMap, messageUuid),
    [sourcesMap, sourceIndexMap, messageUuid],
  );

  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} components={components} className={className}>
      {children}
    </ReactMarkdown>
  );
}

export const MarkdownWithCitations = memo(
  NonMemoizedMarkdownWithCitations,
  (prevProps, nextProps) =>
    prevProps.children === nextProps.children &&
    prevProps.sourcesMap.size === nextProps.sourcesMap.size &&
    prevProps.messageUuid === nextProps.messageUuid &&
    prevProps.className === nextProps.className,
);
